import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ComplexPaginatedDataRequest } from '@app/models/shared.models';
import { LegalEntityService } from '@app/services/admin/legal-entity.service';
import { ToastService } from '@app/services/toast.service';
import { GridTableComponent } from '@app/ui-components/grid-table/grid-table.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import type { ColDef, IDatasource, IGetRowsParams } from 'ag-grid-community';
import { finalize } from 'rxjs';

type Row = {
  id: string;
  name: string;
  email: string;
  taxNumber?: string;
  country?: string;
  deleted?: boolean;
};

@Component({
  selector: 'app-legal-entity-list',
  standalone: true,
  imports: [CommonModule, GridTableComponent, TranslatePipe],
  templateUrl: './legal-entity-list.component.html',
  styleUrl: './legal-entity-list.component.scss',
})
export class LegalEntityListComponent {
  private readonly legalEntityService = inject(LegalEntityService);
  private readonly translate = inject(TranslateService);
  private readonly toast = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  readonly isLoading = signal(true);
  readonly searchValue = signal<string | undefined>(undefined);

  columns: ColDef<Row>[] = [];

  readonly datasource: IDatasource = {
    getRows: (params) => this.loadRows(params),
  };

  ngOnInit(): void {
    this.buildColumns();

    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.buildColumns());
  }

  private buildColumns(): void {
    this.columns = [
      {
        field: 'name',
        headerName: this.translate.instant('admin.legalEntities.table.name'),
        flex: 1,
      },
      {
        field: 'email',
        headerName: this.translate.instant('admin.legalEntities.table.email'),
        flex: 1,
      },
      {
        field: 'taxNumber',
        headerName: this.translate.instant('admin.legalEntities.table.vat'),
        flex: 1,
      },
      {
        field: 'country',
        headerName: this.translate.instant('admin.legalEntities.table.country'),
        maxWidth: 120,
      },
      {
        field: 'deleted',
        headerName: '',
        cellRenderer: (p: any) =>
          p?.data?.deleted
            ? `<span class="badge bg-danger">${this.translate.instant(
                'admin.legalEntities.table.deleted'
              )}</span>`
            : '',
        maxWidth: 140,
        flex: 0,
        editable: false,
        sortable: false,
        cellClass: 'text-center',
      },
    ];
  }

  private loadRows(params: IGetRowsParams): void {
    this.isLoading.set(true);

    const skip = params.startRow;
    const take = params.endRow - params.startRow;
    const sortModel = params.sortModel ?? [];
    const sort: ComplexPaginatedDataRequest['sort'] = sortModel.map((s) => ({
      selector: s.colId,
      desc: s.sort === 'desc',
    }));

    const filter = this.searchValue() ?? '';

    this.legalEntityService
      .getLegalEntitiesAdmin({ skip, take, sort, filter })
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (res) => {
          const data = res.data ?? [];
          const rows: Row[] = data.map((e) => ({
            id: e.id,
            name: e.name,
            email: e.email,
            taxNumber: e.taxNumber,
            country: e.country,
            deleted: e.deleted,
          }));
          const rowCount = res.rowCount ?? rows.length;
          params.successCallback(rows, rowCount);
        },
        error: () => {
          params.failCallback();
          this.toast.error(
            this.translate.instant('admin.toasts.tableLoadFailed')
          );
        },
      });
  }

  onSearchChange(term: string | undefined): void {
    this.searchValue.set(term || undefined);
  }
}
